"use client";

import PageShell from "@/components/PageShell";

import type { ActiveOffer, PublicPlan } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Every static claim on this page is checked against the backend, not against
// the previous copy:
//
//   plans, prices, quotas, seats, trial days  GET /billing/plans (Origin =
//                                             this site) — rendered from the
//                                             data; nothing here hardcodes a
//                                             seat count or a trial. Currency
//                                             is USD for every global plan
//                                             (billing_router.currency_for_org).
//   the joining offer                         GET /billing/active-offer — the
//                                             SignupOffer row signup redeems;
//                                             null means no trial line at all.
//   what every plan includes                  app/api/v1/router.py mounts the
//                                             Todo, Meetings and blog routers
//                                             bare — no plan gate, no edition
//                                             gate — and app/api/mobile_v1/
//                                             router.py re-includes them bare.
//   cancellation                              POST /billing/cancel records a
//                                             request (no gateway call). Global
//                                             subscriptions run on Stripe;
//                                             GET /billing/portal opens the
//                                             Stripe billing portal, and the
//                                             customer.subscription.deleted
//                                             webhook downgrades the org.
//   India-only features                       INDIA_ONLY_FEATURE_KEYS below —
//                                             the seed switches them off on
//                                             every global plan, but a
//                                             struck-through "GST + TDS
//                                             Simulator" is still an Indian
//                                             feature on the global price
//                                             list (§1A), so they are hidden.
//   Enterprise card                           see the comments on it.
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_ORDER: Array<{ key: PublicPlan["features"][number]["category"]; label: string }> = [
  { key: "ai", label: "AI" },
  { key: "vault", label: "Vault" },
  { key: "team", label: "Team" },
  { key: "security", label: "Security" },
  { key: "enterprise", label: "Enterprise" },
];

// Mirror of app/seeds/subscription_plans.py::INDIA_ONLY_FEATURE_KEYS
// (2026-09-18; `dpdp_compliance` added 2026-10-08). Each names an Indian statute, portal, court or regulator; the
// seed sets every one to False on the global plans and the routes behind them
// answer 404 for a global org. `tests/test_plan_editions.py` fails a new
// unclassified key on the backend — when it grows, grow this.
const INDIA_ONLY_FEATURE_KEYS = new Set<string>([
  "tax",
  "traces_access",
  "court_tools",
  "sebi_dashboard",
  "compliance_calendar",
  "marketplace",
  "litigation_risk",
  "gst_tds_simulator",
  "section_138_simulator",
  "court_outcome",
  "court_outcome_beta",
  "damages_quantum",
  "negotiation_simulator",
  "counterfactual_simulator",
  "settlement_simulator",
  "cite_chain_simulator",
  "custom_simulation",
  "tender_intelligence",
  "insurance_legal",
  "reg_change_impact",
  "ai_research",
  "drafting_critic",
  "negotiation_coaching",
  "dpdp_compliance",
]);

// Keys the seed carries that are not a capability anyone can buy:
// `custom_model_training` has no consumer in app/ (the Enterprise card's note
// below), and LexVio trains no model — "AI training" in the product reads the
// firm's documents into house-style preferences, it fine-tunes nothing
// (services/ai_training.py). A struck-through row would still imply it exists.
const NOT_A_CAPABILITY = new Set<string>(["custom_model_training"]);

// Included on every plan — mounted with no plan gate and no edition gate on
// both API surfaces (app/api/v1/router.py, app/api/mobile_v1/router.py):
// todo, meetings, matters/clients/client_shares + the client portal,
// case_studio/case_analysis/drafts/matter_journey, command_center + work,
// contracts/clm + contract_requests, documents, messages, blog. The model
// calls inside them still count against the plan's monthly AI allowance.
const INCLUDED_EVERYWHERE = [
  "Matters, clients and contacts, with the Matter File for every document",
  "Case Intelligence Studio and the Matter Journey — issues, claims, chronology, evidence gaps",
  "Client portal — scoped shares, uploads and approvals, signed in by a one-time email link",
  "Command Centre and My Pending Actions",
  "Contract lifecycle — CLM dashboard, contract requests, obligations, renewals",
  "Tasks, Meeting Intelligence and Messages, linked to the matter",
  "Writing for the LexVio blog — any plan, any role",
];

function formatPrice(p: PublicPlan): { price: string; period: string } {
  if (p.price_usd <= 0) return { price: "Custom", period: "Contact us" };
  const price = `$${p.price_usd.toLocaleString("en-US")}`;
  const period = p.trial_days > 0 ? `per month · ${p.trial_days}-day trial` : "per month, USD";
  return { price, period };
}

function formatLimit(n: number, unit: string): string {
  if (n === -1) return `Unlimited ${unit}`;
  return `${n.toLocaleString("en-US")} ${unit}`;
}

/** Mirrors app/services/signup_offers.py::window_label — one wording for the
 *  same offer on the pricing page, the scan CTA and the welcome email. */
function windowLabel(days: number): string {
  if (days >= 60 && days % 30 === 0) return `${days / 30} months`;
  if (days === 30) return "1 month";
  return `${days} day${days === 1 ? "" : "s"}`;
}

function offerLine(offer: ActiveOffer | null): string {
  if (!offer) {
    return "Billed monthly in USD. No card is taken at sign-up; when a joining offer is running it is stated on the sign-up page.";
  }
  return `New accounts start with ${windowLabel(offer.duration_days)} free and ${offer.tokens_per_month.toLocaleString("en-US")} AI tokens a month — no credit card at sign-up.`;
}

function FeatureRow({
  label,
  enabled,
  featured,
}: {
  label: string;
  enabled: boolean;
  featured: boolean;
}) {
  const muted = featured ? "rgba(251,250,246,.5)" : "rgba(19,18,16,.56)";
  const dim = featured ? "rgba(251,250,246,.22)" : "rgba(19,18,16,.28)";
  return (
    <div
      style={{
        display: "flex",
        gap: "7px",
        fontSize: "12px",
        color: enabled ? muted : dim,
        marginBottom: "8px",
        lineHeight: 1.5,
        textDecoration: enabled ? "none" : "line-through",
      }}
    >
      <span style={{ color: enabled ? (featured ? "var(--cl)" : "var(--cu)") : dim, flexShrink: 0 }}>
        {enabled ? "✓" : "—"}
      </span>
      {label}
    </div>
  );
}

function GroupLabel({ children, featured }: { children: string; featured: boolean }) {
  return (
    <div
      style={{
        fontSize: "9px",
        letterSpacing: ".14em",
        textTransform: "uppercase",
        color: featured ? "rgba(251,250,246,.28)" : "rgba(19,18,16,.32)",
        marginBottom: "8px",
      }}
    >
      {children}
    </div>
  );
}

function PlanCard({ plan, featured }: { plan: PublicPlan; featured: boolean }) {
  const { price, period } = formatPrice(plan);
  const visible = plan.features.filter((f) => !INDIA_ONLY_FEATURE_KEYS.has(f.key) && !NOT_A_CAPABILITY.has(f.key));
  const grouped = CATEGORY_ORDER.map(({ key, label }) => ({
    label,
    feats: visible.filter((f) => f.category === key),
  })).filter((g) => g.feats.length > 0);

  return (
    <div
      style={{
        background: featured ? "var(--o900)" : "var(--b50)",
        border: `1px solid ${featured ? "var(--o800)" : "var(--b200)"}`,
        borderRadius: "6px",
        padding: "30px 24px",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          letterSpacing: ".14em",
          textTransform: "uppercase",
          color: featured ? "rgba(251,250,246,.32)" : "rgba(19,18,16,.36)",
          marginBottom: "12px",
        }}
      >
        {plan.display_name}
        {featured ? " · Most popular" : ""}
      </div>
      <div
        style={{
          fontFamily: "var(--font-fraunces), serif",
          fontSize: "36px",
          fontWeight: 200,
          color: featured ? "var(--b50)" : "var(--o900)",
          letterSpacing: "-.04em",
          marginBottom: "3px",
        }}
      >
        {price}
      </div>
      <div
        style={{
          fontSize: "11px",
          color: featured ? "rgba(251,250,246,.3)" : "rgba(19,18,16,.36)",
          marginBottom: "18px",
        }}
      >
        {period}
      </div>
      <div style={{ height: "1px", background: featured ? "rgba(251,250,246,.09)" : "var(--b200)", marginBottom: "15px" }} />

      <FeatureRow
        enabled
        featured={featured}
        label={formatLimit(plan.ai_scans_per_month, "AI scans / month")}
      />
      <FeatureRow
        enabled
        featured={featured}
        label={plan.storage_gb === -1 ? "Unlimited storage" : `${plan.storage_gb} GB vault`}
      />
      <FeatureRow
        enabled
        featured={featured}
        label={plan.max_users === -1 ? "Unlimited seats" : `${plan.max_users} seat${plan.max_users === 1 ? "" : "s"}`}
      />

      <div style={{ marginTop: "14px" }}>
        <GroupLabel featured={featured}>Included on every plan</GroupLabel>
        {INCLUDED_EVERYWHERE.map((label) => (
          <FeatureRow key={label} enabled featured={featured} label={label} />
        ))}
      </div>

      {grouped.map((group) => (
        <div key={group.label} style={{ marginTop: "14px" }}>
          <GroupLabel featured={featured}>{group.label}</GroupLabel>
          {group.feats.map((f) => (
            <FeatureRow key={f.key} enabled={f.is_enabled} featured={featured} label={f.display_name} />
          ))}
        </div>
      ))}

      <a
        href={`/sign-up?plan=${encodeURIComponent(plan.name)}`}
        style={{
          display: "block",
          marginTop: "22px",
          padding: "10px 18px",
          borderRadius: "3px",
          textAlign: "center",
          fontSize: "12px",
          textDecoration: "none",
          background: featured ? "var(--b50)" : "transparent",
          color: featured ? "var(--o900)" : "rgba(19,18,16,.62)",
          border: featured ? "none" : "1px solid var(--b300)",
        }}
      >
        {plan.trial_days > 0 ? "Start trial" : "Subscribe"}
      </a>
    </div>
  );
}

// The sales-led plan. The seed's `global_team` row (display name "Enterprise",
// price -1, max_users -1) is public, so /billing/plans returns it; render it
// here rather than as a subscribe card with a "Custom" price. Each line is a
// claim, so each carries where it is decided:
//
//   Unlimited seats        max_users -1 on the seed; Super Admin caps per deal.
//   SCIM                   app/api/scim_v2.py is a real RFC 7644 surface, gated
//                          on the `scim` feature which no seeded plan enables —
//                          it is switched on per agreement.
//   SAML SSO               app/api/v1/admin/sso_connectors.py is connector
//                          set-up only; the ACS route its templates advertise
//                          does not exist yet. Say so.
//   On-prem                helm/lexai-enterprise is a scaffold ("full subchart
//                          wiring lands when the first real enterprise customer
//                          lines up") and the runbook it cites is not written.
//                          By arrangement, scoped per engagement — not an
//                          option you tick.
//   Removed                "Custom AI model training" (no such capability:
//                          `custom_model_training` is a feature key with no
//                          consumer and a coming_soon menu item) and
//                          "Air-gapped option" (the AIR_GAPPED setting exists,
//                          the install does not — and air-gapped means no
//                          meeting transcription and no statute verification).
function EnterpriseCard({ plan }: { plan: PublicPlan | undefined }) {
  return (
    <div
      style={{
        background: "var(--b50)",
        border: "1px dashed var(--b300)",
        borderRadius: "6px",
        padding: "30px 24px",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          letterSpacing: ".14em",
          textTransform: "uppercase",
          color: "rgba(19,18,16,.36)",
          marginBottom: "12px",
        }}
      >
        {plan?.display_name ?? "Enterprise"}
      </div>
      <div
        style={{
          fontFamily: "var(--font-fraunces), serif",
          fontSize: "36px",
          fontWeight: 200,
          color: "var(--o900)",
          letterSpacing: "-.04em",
          marginBottom: "3px",
        }}
      >
        Custom
      </div>
      <div style={{ fontSize: "11px", color: "rgba(19,18,16,.36)", marginBottom: "18px" }}>
        seats and quota set per agreement · annual contract
      </div>
      <div style={{ height: "1px", background: "var(--b200)", marginBottom: "15px" }} />
      <FeatureRow enabled featured={false} label="Multi-seat, with roles — seat count set per agreement" />
      <FeatureRow enabled featured={false} label="SCIM provisioning (RFC 7644), switched on per agreement" />
      <FeatureRow enabled featured={false} label="SAML SSO: IdP connector set-up today; IdP sign-in in progress" />
      <FeatureRow enabled featured={false} label="Self-hosted / on-prem deployment, scoped per engagement" />
      <FeatureRow enabled featured={false} label="Custom workflow agents" />
      <FeatureRow enabled featured={false} label="Dedicated success manager" />
      <div style={{ marginTop: "14px" }}>
        <GroupLabel featured={false}>Included on every plan</GroupLabel>
        {INCLUDED_EVERYWHERE.map((label) => (
          <FeatureRow key={label} enabled featured={false} label={label} />
        ))}
      </div>
      <a
        href="/contact?topic=enterprise"
        style={{
          display: "block",
          marginTop: "22px",
          padding: "10px 18px",
          borderRadius: "3px",
          textAlign: "center",
          fontSize: "12px",
          textDecoration: "none",
          background: "var(--o900)",
          color: "var(--b50)",
          border: "none",
        }}
      >
        Contact sales →
      </a>
    </div>
  );
}

export default function PricingClient({
  plans,
  stale,
  offer,
}: {
  plans: PublicPlan[];
  stale: boolean;
  offer: ActiveOffer | null;
}) {
  // price -1 is the seed's "contact sales" — the Enterprise plan. It gets the
  // contact card, not a subscribe card.
  const selfServe = plans.filter((p) => p.price_usd > 0);
  const contactPlan = plans.find((p) => p.price_usd <= 0);
  const featuredName = selfServe.some((p) => p.name === "global_pro")
    ? "global_pro"
    : selfServe.some((p) => p.name === "pro")
      ? "pro"
      : selfServe[selfServe.length - 1]?.name;

  return (
    <PageShell>
      <section className="simple-hero" style={{ padding: "72px clamp(20px,4vw,52px) 60px", borderBottom: "1px solid var(--b200)" }}>
        <div className="ey rev">Pricing</div>
        <h1 className="sh2 rev d1" style={{ fontSize: "clamp(36px,4.5vw,58px)" }}>
          Pricing that makes sense{" "}
          <br />
          <em>for teams everywhere.</em>
        </h1>
        <p className="rev d2" style={{ fontSize: "16px", color: "rgba(19,18,16,.55)", marginTop: "16px", maxWidth: "640px" }}>
          {offerLine(offer)}
        </p>
        <p className="rev d3" style={{ fontSize: "13px", color: "rgba(19,18,16,.45)", marginTop: "10px", maxWidth: "640px", lineHeight: 1.7 }}>
          Matters, the Case Intelligence Studio, the client portal, the Command Centre, the contract lifecycle, Tasks, Meeting Intelligence and Messages are on every plan, on the web and the phone; the Privacy & Data Protection Programme is on Pro and above. Meeting transcription uses a cloud vendor and is unavailable on an air-gapped install. Every plan is charged in USD through Stripe, wherever you are: if your account is registered in another currency, your bank applies its own conversion rate — we never quote a converted price.
        </p>
        {stale ? (
          <p style={{ fontSize: "12px", color: "rgba(19,18,16,.4)", marginTop: "12px" }}>
            Showing cached plans — live pricing temporarily unavailable.
          </p>
        ) : null}
      </section>

      <section style={{ padding: "44px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${selfServe.length + 1}, 1fr)`,
            gap: "14px",
          }}
        >
          {selfServe.map((p) => (
            <PlanCard key={p.id} featured={p.name === featuredName} plan={p} />
          ))}
          <EnterpriseCard plan={contactPlan} />
        </div>
      </section>

      <section style={{ padding: "72px clamp(20px,4vw,52px)", textAlign: "center", background: "var(--o950)" }}>
        <h2
          style={{
            fontFamily: "var(--font-fraunces), serif",
            fontSize: "clamp(28px,3.5vw,42px)",
            fontWeight: 200,
            color: "var(--b50)",
            marginBottom: "14px",
          }}
        >
          Get started.{" "}
          <br />
          <em style={{ color: "var(--cl)", fontStyle: "italic" }}>Upgrade anytime.</em>
        </h2>
        <p style={{ fontSize: "15px", color: "rgba(251,250,246,.35)", marginBottom: "28px", maxWidth: "620px", marginLeft: "auto", marginRight: "auto", lineHeight: 1.7 }}>
          Billed monthly in USD, no lock-in. Upgrade from Billing at any time; to stop a subscription, use the Stripe billing portal from your Billing page or write to support before your renewal date, and it ends at the close of the paid period.
        </p>
        <a className="cta-p" data-mag="true" href={`/sign-up`}>
          <span className="cta-p-t">Get started →</span>
        </a>
      </section>
    </PageShell>
  );
}
